import Decimal from 'decimal.js-light';

import { AllocationType } from 'common/src/types/enums/AllocationType.enum';
import MONTHS from 'common/src/constants/months';

import { IAllocationTemplateDetail } from '../types/interfaces';

export type AllocationValidationResult = {
  success: boolean;
  errors?: string[];
};

export function validateAllocationTemplatePercents(
  type: AllocationType,
  details: IAllocationTemplateDetail[],
): AllocationValidationResult {
  if (type === AllocationType.MonthlyPercentage) {
    const detailsByMonth = new Map<number, IAllocationTemplateDetail[]>();

    for (const detail of details) {
      detailsByMonth.set(
        detail.budgetMonth,
        (detailsByMonth.get(detail.budgetMonth) ?? []).concat([detail]),
      );
    }

    if (detailsByMonth.size !== 12) {
      return {
        success: false,
        errors: [
          'Monthly percentage allocation templates must have details for all 12 months.',
        ],
      };
    }

    let success = true;
    const errors = [];
    for (const [month, monthlyDetails] of detailsByMonth.entries()) {
      const { success: monthSuccess, errors: monthErrors } = validatePercents(
        monthlyDetails,
        month,
      );
      success = success && monthSuccess;
      if (monthErrors?.length) {
        errors.push(...monthErrors);
      }
    }

    return {
      success,
      errors: errors.length ? errors : undefined,
    };
  }

  return validatePercents(details);
}

function validatePercents(
  details: IAllocationTemplateDetail[],
  allocationMonth?: number,
): AllocationValidationResult {
  let hasCostPercents = true;
  let hasUsagePercents = true;

  for (const detail of details) {
    if (detail.costPercent == null) {
      hasCostPercents = false;
    }
    if (detail.usagePercent == null) {
      hasUsagePercents = false;
    }

    if (!hasCostPercents && !hasUsagePercents) {
      break;
    }
  }

  if (hasCostPercents && hasUsagePercents) {
    const [costSuccess, costError] = percentsSumTo100(
      details,
      'costPercent',
      allocationMonth,
    );
    const [usageSuccess, usageError] = percentsSumTo100(
      details,
      'usagePercent',
      allocationMonth,
    );

    const errors = [];
    if (!costSuccess) {
      errors.push(costError);
    }
    if (!usageSuccess) {
      errors.push(usageError);
    }
    return {
      success: costSuccess && usageSuccess,
      errors: errors.length ? errors : undefined,
    };
  }

  return {
    success: false,
    errors: [
      `Allocation template details must all have a total percent or cost and usage percentages${
        allocationMonth !== undefined ? ` for ${MONTHS[allocationMonth]}` : ''
      }`,
    ],
  };
}

function percentsSumTo100(
  details: IAllocationTemplateDetail[],
  percentKey: keyof Pick<
    IAllocationTemplateDetail,
    'costPercent' | 'usagePercent'
  >,
  allocationMonth: number,
): [boolean, string] {
  const total = details
    .reduce((sum, d) => sum.add(d[percentKey]), new Decimal(0))
    .toNumber();

  const equals100 = total === 100;
  const error = equals100
    ? undefined
    : `Total percents from details '${percentKey}' key must equal 100%${
        allocationMonth !== undefined ? ` for ${MONTHS[allocationMonth]}` : ''
      }, calculated total was ${total}%.`;
  return [equals100, error];
}
